// GDPR / ePrivacy consent model.
//
// The banner lives in the app shell (not a pilet) because consent is
// cross-cutting infrastructure: it must appear on first load, independently of
// the remote pilet feed, and it gates analytics/marketing for the whole app.

/** Cookie categories the user can decide on. `necessary` is always granted. */
export type ConsentCategory = 'necessary' | 'analytics' | 'marketing'

/** The user's per-category decision. `necessary` is structurally always true. */
export interface ConsentChoices {
  necessary: true
  analytics: boolean
  marketing: boolean
}

/**
 * The persisted, auditable record of a consent decision.
 *
 * `version` + `decidedAt` let us re-ask when the cookie policy changes or the
 * consent expires (CNIL guidance: re-collect after ~6 months). Keeping the
 * timestamp is also what lets us later prove *when* consent was given.
 */
export interface ConsentRecord {
  version: number
  decidedAt: string // ISO-8601
  choices: ConsentChoices
}

/** Everything a non-necessary category defaults to before an explicit choice. */
export const REJECTED_CHOICES: ConsentChoices = {
  necessary: true,
  analytics: false,
  marketing: false,
}

export const GRANTED_CHOICES: ConsentChoices = {
  necessary: true,
  analytics: true,
  marketing: true,
}

/** Bump when the cookie policy / category set changes — forces re-consent. */
export const CONSENT_VERSION = 1

/** Re-ask for consent after this long. ~6 months, per CNIL recommendation. */
export const CONSENT_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 182

/**
 * LocalStorage key (shared instance via use-persisted-state in the shell). Defined in
 * the shell's storage inventory (src/storage.ts) and re-exported here so the consent
 * module's importers keep resolving it from one place.
 */
export { CONSENT_STORAGE_KEY } from '../storage'

/** Window event other scripts (analytics loaders) can listen to. */
export const CONSENT_EVENT = 'medialib:consent'

export interface ConsentCategoryMeta {
  id: Exclude<ConsentCategory, 'necessary'> | 'necessary'
  title: string
  description: string
  /** Necessary cookies cannot be toggled off. */
  required: boolean
}

/** Human-readable catalogue shown in the "manage preferences" panel (fr-CH). */
export const CONSENT_CATEGORIES: ConsentCategoryMeta[] = [
  {
    id: 'necessary',
    title: 'Strictement nécessaires',
    description:
      'Indispensables au fonctionnement du site (sécurité, session, mémorisation de vos choix de cookies). Ils ne peuvent pas être désactivés.',
    required: true,
  },
  {
    id: 'analytics',
    title: 'Mesure d’audience',
    description:
      'Nous aident à comprendre l’usage du site (pages vues, performances) afin de l’améliorer. Données agrégées et anonymisées.',
    required: false,
  },
  {
    id: 'marketing',
    title: 'Personnalisation & marketing',
    description:
      'Permettent de personnaliser les contenus et recommandations, et de mesurer l’efficacité de nos campagnes.',
    required: false,
  },
]
